import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../api.js';
import type { ÉtatSession } from '../api.js';

interface Props {
  onSession: (état: ÉtatSession) => void;
}

/**
 * Enrôlement du second facteur, obligatoire à la première connexion.
 *
 * Le QR code est fabriqué **dans le navigateur** à partir de l'URI otpauth : le secret ne part
 * vers aucun service de génération d'images. C'est ce qui permet à la CSP de rester en 'self'
 * strict, et cela évite d'exposer un secret d'authentification à un tiers.
 */
export function EnrolementTotp({ onSession }: Props) {
  const [uri, setUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    let annulé = false;
    void (async () => {
      const { status, data } = await api.démarrerEnrôlement();
      if (annulé) return;
      if (status !== 200) {
        setErreur(data.error ?? 'Enrôlement impossible');
        return;
      }
      setUri(data.otpauthUri);
      setSecret(data.secretBase32);
      const svg = await QRCode.toString(data.otpauthUri, {
        type: 'svg',
        margin: 1,
        errorCorrectionLevel: 'M',
      });
      if (!annulé) setQr(svg);
    })();
    return () => {
      annulé = true;
    };
  }, []);

  async function confirmer(événement: React.FormEvent) {
    événement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const { status, data } = await api.confirmerEnrôlement(code);
      if (status !== 200) {
        setErreur(data.error ?? 'Code refusé');
        setCode('');
        return;
      }
      onSession(data);
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <main className="coquille coquille--étroite">
      <header>
        <h1>Vigie</h1>
        <p className="sous-titre">Activation du second facteur</p>
      </header>

      <div className="carte">
        <h2>Double authentification obligatoire</h2>
        <p className="aide">
          Scanne ce code avec ton application d’authentification (Aegis, FreeOTP, ou équivalent),
          puis saisis le code affiché pour confirmer.
        </p>

        {qr !== null && (
          // Le SVG est produit localement à partir de l'URI, jamais reçu d'un tiers.
          <div className="qr" dangerouslySetInnerHTML={{ __html: qr }} />
        )}

        {secret !== null && (
          <details className="repli">
            <summary>Impossible de scanner ?</summary>
            <p className="aide">Saisis cette clé manuellement dans ton application :</p>
            <code className="secret">{secret.replace(/(.{4})/g, '$1 ').trim()}</code>
          </details>
        )}

        <form onSubmit={(e) => void confirmer(e)}>
          <label htmlFor="code-enrolement">Code de confirmation</label>
          <input
            id="code-enrolement"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          {erreur !== null && <p className="erreur">{erreur}</p>}
          <button type="submit" disabled={enCours || code.length !== 6 || uri === null}>
            {enCours ? 'Vérification…' : 'Activer'}
          </button>
        </form>
      </div>
    </main>
  );
}
